import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './LeaveApprovalList.css';
import jsPDF from "jspdf";
import "jspdf-autotable";
import ExcelJS from 'exceljs';
import { selectStyles } from '../../../assets/Styles/selectStyle';
import { toWords } from 'number-to-words';
import fgIdentitySolutions from '../../../assets/Images/fgIdentitySolutions.jpg';
import { saveAs } from 'file-saver';
import { getDistrict, getProject, getStates, getLeavesEmployees, getClients } from '../../../services/monthlyAttendanceService';
import { capitalizeFirstLetter } from '../../../utils/validations';
import { getFilteredApprovedLeavesReport } from '../../../services/leaveService';

const LeaveApprovalList = () => {
    const empRole = localStorage.getItem('role');
    const distctLoginUser = localStorage.getItem('districtId');
    const loggedInDistrictName = localStorage.getItem('districtName');
    const loggedInStateId = localStorage.getItem('stateId');
    const loggedInStateName = localStorage.getItem('stateName');
    const designationName = localStorage.getItem('designationName'); // Get designation from localStorage

    const [state, setState] = useState(empRole === "ITM" ? (loggedInStateId || 3) : null);
    const [district, setDistrict] = useState(empRole === "ITM" ? distctLoginUser : null);
    const [districtName, setDistrictName] = useState(loggedInDistrictName);
    const [designation, setDesignation] = useState();
    const [year, setYear] = useState(null);
    const [stateOptions, setStateOptions] = useState([]);
    const [districtOptions, setDistrictOptions] = useState([]);
    const [employeeOptions, setEmployeeOptions] = useState([]);
    const [attendanceData, setAttendanceData] = useState([]);
    const [projectResponse, setProjectResponse] = useState([]);
    const [employee, setEmployee] = useState(null);
    const [fromDate, setFromDate] = useState("");
    const dateInputRef = useRef(null);
    const [toDate, setToDate] = useState("");
    const effectRan = useRef(false);
    const [project, setProject] = useState(() => {
        // Auto-set project based on designation for ITM role
        if (empRole === "ITM") {
            if (designationName === "ITM_TPDS") return 1;
            if (designationName === "ITM_PPC") return 2;
        }
        return null;
    });
    const [clientResponse, setClientResponse] = useState([]);
    const [clients, setClients] = useState(null);
    const [isFocused, setIsFocused] = useState({
        state: false,
        district: false,
        designation: false,
        fromDate: false,
        employee: false,
        toDate: false,
        clients: false,
        project: false,
    });
    const [errors, setErrors] = useState({});
    const [hasFetched, setHasFetched] = useState(false);

    // For non-ITM role, client is required; for ITM, only date range is required
    const isFetchDataEnabled = empRole === "ITM"
        ? (state && fromDate && toDate)
        : (state && clients && fromDate && toDate);

    // Fetch states
    useEffect(() => {
        const fetchStates = async () => {
            try {
                const data = await getStates();
                const options = data.map(item => ({
                    value: item.id,
                    label: capitalizeFirstLetter(item.stateName),
                }));
                setStateOptions(options);

                // For ITM role, set the state dropdown to show only the logged-in state
                if (empRole === "ITM" && loggedInStateId) {
                    const userState = options.find(opt => opt.value === Number(loggedInStateId));
                    if (userState) {
                        setStateOptions([userState]);
                    }
                }
            } catch (error) {
                console.error('Error fetching states:', error);
                setErrors(prev => ({ ...prev, general: 'Failed to load states' }));
            }
        };
        if (!effectRan.current) {
            fetchStates();
        }
        return () => {
            effectRan.current = true;
        }
    }, [empRole, loggedInStateId]);

    // Fetch districts when state changes
    useEffect(() => {
        if (state) {
            const fetchDistricts = async () => {
                try {
                    const data = await getDistrict(state);
                    const allDistricts = data.map(item => ({
                        value: item.districtId,
                        label: item.districtName,
                    }));

                    if (empRole === "ITM" && distctLoginUser) {
                        const userDistrict = allDistricts.filter(
                            d => d.value === Number(distctLoginUser)
                        );
                        setDistrictOptions(userDistrict);
                    } else {
                        setDistrictOptions(allDistricts);
                    }
                } catch (error) {
                    console.error('Error fetching districts:', error);
                    setErrors(prev => ({ ...prev, general: 'Failed to load districts' }));
                }
            };
            fetchDistricts();
        } else {
            setDistrictOptions([]);
            setDistrict(null);
        }
    }, [state, empRole, distctLoginUser]);

    useEffect(() => {
        if (state) {
            const fetchClients = async () => {
                try {
                    const data = await getClients(state);
                    setClientResponse(
                        data.map(client => ({
                            value: client.id,
                            label: client.clientName,
                        }))
                    );
                } catch (error) {
                    console.error('Error fetching clients:', error);
                    setErrors(prev => ({ ...prev, clients: 'Failed to load clients' }));
                }
            };
            fetchClients();
        } else {
            setClientResponse([]);
            setClients(null);
        }
    }, [state]);

    useEffect(() => {
        if (clients) {
            const fetchProjects = async () => {
                try {
                    const data = await getProject(clients);
                    let projects = data.map(pro => ({
                        value: pro.id,
                        label: pro.projectCode,
                    }));

                    // For ITM role, filter projects based on designation
                    if (empRole === "ITM") {
                        if (designationName === "ITM_TPDS") {
                            projects = projects.filter(p => p.value === 1);
                        } else if (designationName === "ITM_PPC") {
                            projects = projects.filter(p => p.value === 2);
                        }
                    }

                    setProjectResponse(projects);
                } catch (error) {
                    console.error('Error fetching projects:', error);
                    setErrors(prev => ({ ...prev, general: 'Failed to load projects' }));
                }
            };
            fetchProjects();
        } else {
            setProjectResponse([]);
        }
    }, [clients, empRole, designationName]);

    // Fetch employees when state, clients, or district changes
    useEffect(() => {
        if (state && clients) {
            const fetchEmployee = async () => {
                try {
                    const response = await getLeavesEmployees(state, clients, district);
                    setEmployeeOptions(
                        response.map(emp => ({
                            value: emp.employeeUniqueId,
                            label: emp.employeeName,
                            designation: emp.designation,
                            employeeId: emp.employeeId
                        }))
                    );
                } catch (error) {
                    console.error('Error fetching employees:', error);
                    setErrors(prev => ({ ...prev, general: 'Failed to load employees' }));
                }
            };
            fetchEmployee();
        } else {
            setEmployeeOptions([]);
            setEmployee(null);
        }
    }, [state, clients, district]);

    // Clear attendance data when any dropdown changes
    useEffect(() => {
        setAttendanceData([]);
        setHasFetched(false);
    }, [state, district, designation, year, employee, clients, project]);

    const handleInputChange = (selectedOption, field) => {
        const value = selectedOption ? selectedOption.value : null;
        switch (field) {
            case 'state':
                setState(value);
                setDistrict(null);
                setClients(null);
                setProject(null);
                break;
            case 'district':
                setDistrict(value);
                break;
            case 'designation':
                setDesignation(value);
                break;
            case 'employee':
                setEmployee(value);
                if (selectedOption) {
                    const emp = employeeOptions.find(emp => emp.value === selectedOption.value);
                    setDesignation(emp ? emp.designation : '');
                } else {
                    setDesignation('');
                }
                break;
            case 'clients':
                setClients(value);
                setProject(null);
                setEmployee(null);
                break;
            case 'project':
                setProject(value);
                break;
            default:
                break;
        }
        setErrors(prev => ({ ...prev, [field]: null }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!state) newErrors.state = 'State is required';

        if (empRole !== "ITM" && !clients) newErrors.clients = 'Client is required';

        if (!fromDate) newErrors.fromDate = 'From Date is required';
        if (!toDate) newErrors.toDate = 'To Date is required';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleFetchReport = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            const filters = {
                stateId: state,
                districtId: district,
                employeeUniqueId: employee,
                clientId: clients,
                projectId: project, // This will be auto-set for ITM_TPDS (1) and ITM_PPC (2)
                fromDate: fromDate,
                toDate: toDate,
            };

            console.log("Sending filters:", filters); // Debug log to verify projectId

            const data = await getFilteredApprovedLeavesReport(filters);
            setAttendanceData(data);
        } catch (error) {
            console.error("Error fetching report data:", error);
            setErrors((prev) => ({
                ...prev,
                general: "Failed to fetch report data",
            }));
            alert("Failed to fetch report data. Please try again.");
        }
    };

    const handleDownloadExcel = async () => {
        if (attendanceData.length === 0) {
            alert("No data available to download. Please fetch the report first.");
            return;
        }

        try {
            const workbook = new ExcelJS.Workbook();
            workbook.creator = 'Leave Report System';
            workbook.created = new Date();

            const worksheet = workbook.addWorksheet('Leave Report', {
                views: [{ state: 'frozen', ySplit: 8 }] // Freeze top 8 rows
            });

            // ===== 1. Title Row =====
            const titleRow = worksheet.addRow(['Leave Report']);
            titleRow.font = { size: 16, bold: true, color: { argb: 'FFFFFFFF' } };
            titleRow.alignment = { horizontal: 'center', vertical: 'middle' };
            titleRow.height = 28;
            worksheet.mergeCells(`A1:M1`);
            titleRow.eachCell((cell) => {
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FF1F4E78' }
                };
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' }
                };
            });

            // ===== 2. Report Generation Date & Time =====
            const now = new Date();
            const generatedOn = now.toLocaleDateString('en-GB', {
                day: '2-digit', month: '2-digit', year: 'numeric'
            });
            const generatedAt = now.toLocaleTimeString('en-GB', {
                hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
            });

            const dateRow = worksheet.addRow([
                `Report Generated On: ${generatedOn}   |   Time: ${generatedAt}`
            ]);
            dateRow.font = { size: 11, italic: true, bold: true, color: { argb: 'FF333333' } };
            dateRow.alignment = { horizontal: 'right', vertical: 'middle' };
            dateRow.height = 20;
            worksheet.mergeCells(`A2:M2`);

            // ===== 3. Selected Filters Section =====
            const stateLabel = stateOptions.find(o => o.value === Number(state))?.label || 'All';
            const districtLabel = districtOptions.find(o => o.value === Number(district))?.label || 'All';
            const clientLabel = clientResponse.find(o => o.value === clients)?.label || 'N/A';
            const projectLabel = projectResponse.find(o => o.value === project)?.label || 'N/A';
            const employeeLabel = employeeOptions.find(o => o.value === employee)?.label || 'All';
            const designationLabel = designation || 'All';

            // Filter header row
            const filterHeaderRow = worksheet.addRow(['Selected Filters']);
            filterHeaderRow.font = { size: 12, bold: true, color: { argb: 'FFFFFFFF' } };
            filterHeaderRow.alignment = { horizontal: 'center', vertical: 'middle' };
            filterHeaderRow.height = 22;
            worksheet.mergeCells(`A3:M3`);
            filterHeaderRow.eachCell((cell) => {
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FF2E75B6' }
                };
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' }
                };
            });

            // Filter details row 1
            const filterRow1 = worksheet.addRow([
                `State: ${stateLabel}`,
                `District: ${districtLabel}`,
                `Client: ${clientLabel}`,
                `Project: ${projectLabel}`
            ]);
            worksheet.mergeCells(`A4:C4`);
            worksheet.mergeCells(`D4:F4`);
            worksheet.mergeCells(`G4:I4`);
            worksheet.mergeCells(`J4:M4`);
            filterRow1.font = { size: 11, bold: false };
            filterRow1.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
            filterRow1.height = 20;

            // Filter details row 2
            const filterRow2 = worksheet.addRow([
                `Employee: ${employeeLabel}`,
                `Designation: ${designationLabel}`,
                `From Date: ${fromDate ? fromDate.split('-').reverse().join('-') : 'N/A'}`,
                `To Date: ${toDate ? toDate.split('-').reverse().join('-') : 'N/A'}`
            ]);
            worksheet.mergeCells(`A5:C5`);
            worksheet.mergeCells(`D5:F5`);
            worksheet.mergeCells(`G5:I5`);
            worksheet.mergeCells(`J5:M5`);
            filterRow2.font = { size: 11, bold: false };
            filterRow2.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
            filterRow2.height = 20;

            // Spacer row
            worksheet.addRow([]);
            worksheet.getRow(6).height = 8;

            // ===== 4. Table Header =====
            const headers = [
                'S.No', 'District Name', 'Employee Name', 'Mobile Number',
                'Designation', 'Client', 'Project', 'From Date', 'To Date',
                'Days Applied', 'Leave Type', 'Reason', 'Status'
            ];

            const headerRow = worksheet.addRow(headers);
            headerRow.height = 26;
            headerRow.eachCell((cell) => {
                cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
                cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FF4472C4' }
                };
                cell.border = {
                    top: { style: 'thin', color: { argb: 'FF000000' } },
                    left: { style: 'thin', color: { argb: 'FF000000' } },
                    bottom: { style: 'thin', color: { argb: 'FF000000' } },
                    right: { style: 'thin', color: { argb: 'FF000000' } }
                };
            });

            // ===== 5. Data Rows =====
            attendanceData.forEach((item, index) => {
                const row = worksheet.addRow([
                    index + 1,
                    item.district || '',
                    item.employeeName || '',
                    item.contactNumber || '',
                    item.designation || '',
                    item.clientName || '',
                    item.projectName || '',
                    item.fromDate ? item.fromDate.split('-').reverse().join('-') : '',
                    item.toDate ? item.toDate.split('-').reverse().join('-') : '',
                    item.daysApplied || '',
                    item.type || '',
                    item.reason || '',
                    item.status || ''
                ]);

                row.height = 20;
                row.eachCell((cell, colNumber) => {
                    cell.alignment = {
                        horizontal: colNumber === 1 || colNumber === 10 ? 'center' : 'left',
                        vertical: 'middle',
                        wrapText: true
                    };
                    cell.font = { size: 10 };
                    cell.border = {
                        top: { style: 'thin', color: { argb: 'FFD0D0D0' } },
                        left: { style: 'thin', color: { argb: 'FFD0D0D0' } },
                        bottom: { style: 'thin', color: { argb: 'FFD0D0D0' } },
                        right: { style: 'thin', color: { argb: 'FFD0D0D0' } }
                    };
                    // Zebra striping
                    if (index % 2 === 1) {
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: 'FFF2F2F2' }
                        };
                    }
                });
            });

            // ===== 6. Column Widths =====
            const columnWidths = [8, 18, 22, 15, 18, 20, 18, 14, 14, 12, 14, 30, 12];
            columnWidths.forEach((width, i) => {
                worksheet.getColumn(i + 1).width = width;
            });

            // ===== 7. Auto Filter on header =====
            worksheet.autoFilter = {
                from: { row: 7, column: 1 },
                to: { row: 7, column: 13 }
            };

            // ===== 8. Save File =====
            const fileName = `Leave_Report_${generatedOn.replace(/\//g, '-')}_${generatedAt.replace(/:/g, '-')}.xlsx`;
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            saveAs(blob, fileName);

        } catch (error) {
            console.error('Error generating Excel:', error);
            alert('Failed to generate Excel file. Please try again.');
        }
    };
    return (
        <div className="leave-approval-container">
            <div className="leave-approval-box">
                <h2 className="leave-approval-heading">Leave Report</h2>
                {errors.general && <div className="error-message">{errors.general}</div>}
                <form onSubmit={handleFetchReport}>
                    <div className="leave-approval-maincontainer">
                        <div className="leave-approval-container-part">
                            <div className={`leave-approval-floating-label-container ${isFocused.state || state ? 'float-label' : ''}`}>
                                <Select
                                    name="state"
                                    options={stateOptions}
                                    value={stateOptions.find(opt => opt.value === Number(state)) || null}
                                    onChange={option => handleInputChange(option, 'state')}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, state: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, state: false }))}
                                    placeholder=" "
                                    isClearable={empRole !== "ITM"}
                                    menuPortalTarget={document.body}
                                    styles={selectStyles}
                                    isDisabled={empRole === "ITM"}
                                />
                                <label><span className="required">*</span>State</label>
                                {errors.state && <div className="error-message">{errors.state}</div>}
                            </div>
                        </div>
                        <div className="leave-approval-container-part">
                            <div className={`leave-approval-floating-label-container ${isFocused.district || district ? 'float-label' : ''}`}>
                                <Select
                                    name="district"
                                    options={districtOptions}
                                    value={districtOptions.find(opt => opt.value === Number(district)) || null}
                                    onChange={option => handleInputChange(option, 'district')}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, district: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, district: false }))}
                                    placeholder=" "
                                    isClearable={empRole !== "ITM"}
                                    menuPortalTarget={document.body}
                                    styles={selectStyles}
                                    isDisabled={empRole === "ITM"}
                                />
                                <label>District</label>
                                {errors.district && <div className="error-message">{errors.district}</div>}
                            </div>
                        </div>
                    </div>

                    {/* Show Client, Project, Employee sections only for non-ITM roles */}
                    {empRole !== "ITM" && (
                        <>
                            <div className="leave-approval-maincontainer">
                                <div className="leave-approval-container-part">
                                    <div className={`leave-approval-floating-label-container ${isFocused.clients || clients ? 'float-label' : ''}`}>
                                        <Select
                                            name="clients"
                                            options={clientResponse}
                                            value={clientResponse.find(opt => opt.value === clients) || null}
                                            onChange={option => handleInputChange(option, 'clients')}
                                            onFocus={() => setIsFocused(prev => ({ ...prev, clients: true }))}
                                            onBlur={() => setIsFocused(prev => ({ ...prev, clients: false }))}
                                            placeholder=" "
                                            isClearable
                                            menuPortalTarget={document.body}
                                            styles={selectStyles}
                                        />
                                        <label><span className="required">*</span>Client</label>
                                        {errors.clients && <div className="error-message">{errors.clients}</div>}
                                    </div>
                                </div>

                                <div className="leave-approval-container-part">
                                    <div className={`leave-approval-floating-label-container ${isFocused.project || project ? 'float-label' : ''}`}>
                                        <Select
                                            name="project"
                                            options={projectResponse}
                                            value={projectResponse.find(opt => opt.value === project) || null}
                                            onChange={option => handleInputChange(option, 'project')}
                                            onFocus={() => setIsFocused(prev => ({ ...prev, project: true }))}
                                            onBlur={() => setIsFocused(prev => ({ ...prev, project: false }))}
                                            placeholder=" "
                                            isClearable
                                            menuPortalTarget={document.body}
                                            styles={selectStyles}
                                        />
                                        <label>Project</label>
                                        {errors.project && <div className="error-message">{errors.project}</div>}
                                    </div>
                                </div>
                            </div>
                            <div className="leave-approval-maincontainer">
                                <div className="leave-approval-container-part">
                                    <div className={`leave-approval-floating-label-container ${isFocused.employee || employee ? 'float-label' : ''}`}>
                                        <Select
                                            name="employee"
                                            options={employeeOptions}
                                            value={employeeOptions.find(opt => opt.value === employee) || null}
                                            onChange={option => handleInputChange(option, 'employee')}
                                            onFocus={() => setIsFocused(prev => ({ ...prev, employee: true }))}
                                            onBlur={() => setIsFocused(prev => ({ ...prev, employee: false }))}
                                            placeholder=" "
                                            isClearable
                                            menuPortalTarget={document.body}
                                            styles={selectStyles}
                                        />
                                        <label>Employee Name</label>
                                        {errors.employee && <div className="error-message">{errors.employee}</div>}
                                    </div>
                                </div>

                                <div className="leave-approval-container-part">
                                    <div className={`leave-approval-floating-label-container ${isFocused.designation || designation ? 'float-label' : ''}`}>
                                        <input
                                            type="text"
                                            name="designation"
                                            value={designation || ""}
                                            onChange={(e) => {
                                                setDesignation(e.target.value);
                                                setErrors(prev => ({ ...prev, designation: null }));
                                            }}
                                            onFocus={() => setIsFocused(prev => ({ ...prev, designation: true }))}
                                            onBlur={() => setIsFocused(prev => ({ ...prev, designation: false }))}
                                            placeholder=" "
                                            className="did-floating-input"
                                            readOnly
                                            disabled
                                        />
                                        <label>Designation</label>
                                        {errors.designation && <div className="error-message">{errors.designation}</div>}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {/* For ITM role, show a read-only project field to indicate which project is being used */}
                    {/* {empRole === "ITM" && (designationName === "ITM_TPDS" || designationName === "ITM_PPC") && (
                        <div className="leave-approval-maincontainer">
                            <div className="leave-approval-container-part">
                                <div className={`leave-approval-floating-label-container float-label`}>
                                    <input
                                        type="text"
                                        value={designationName === "ITM_TPDS" ? "Project: TPDS (ID: 1)" : "Project: PPC (ID: 2)"}
                                        className="did-floating-input"
                                        readOnly
                                        disabled
                                    />
                                    <label>Auto-Assigned Project</label>
                                </div>
                            </div>
                        </div>
                    )} */}

                    <div className="approval-maincontainer">
                        {/* From Date */}
                        <div className="approval-container-part">
                            <div className={`approval-label-container-2 ${isFocused.fromDate || fromDate ? 'float-label' : ''}`}>
                                <DatePicker
                                    selected={fromDate ? new Date(fromDate) : null}
                                    onChange={(date) => {
                                        if (date) {
                                            const localDate = new Date(date);
                                            localDate.setHours(0, 0, 0, 0);
                                            const formatted = localDate.toLocaleDateString("en-CA");
                                            setFromDate(formatted);
                                        } else {
                                            setFromDate("");
                                        }
                                    }}
                                    dateFormat="dd-MM-yyyy"
                                    placeholderText="dd-MM-yyyy"
                                    className="did-floating-input"
                                    onFocus={() => setIsFocused(prev => ({ ...prev, fromDate: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, fromDate: false }))}
                                    ref={dateInputRef}
                                    showYearDropdown
                                    scrollableYearDropdown
                                    yearDropdownItemNumber={35}
                                />
                                <label>
                                    <span className="required">*</span>From Date (DD-MM-YYYY)
                                </label>
                                {errors.fromDate && <div className="error-message">{errors.fromDate}</div>}
                            </div>
                        </div>

                        {/* To Date */}
                        <div className="approval-container-part">
                            <div className={`approval-label-container-2 ${isFocused.toDate || toDate ? 'float-label' : ''}`}>
                                <DatePicker
                                    selected={toDate ? new Date(toDate) : null}
                                    onChange={(date) => {
                                        if (date) {
                                            const localDate = new Date(date);
                                            localDate.setHours(0, 0, 0, 0);
                                            const formatted = localDate.toLocaleDateString("en-CA");
                                            setToDate(formatted);
                                        } else {
                                            setToDate("");
                                        }
                                    }}
                                    dateFormat="dd-MM-yyyy"
                                    placeholderText="dd-MM-yyyy"
                                    className="did-floating-input"
                                    onFocus={() => setIsFocused(prev => ({ ...prev, toDate: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, toDate: false }))}
                                    ref={dateInputRef}
                                    showYearDropdown
                                    scrollableYearDropdown
                                    yearDropdownItemNumber={35}
                                />
                                <label>
                                    <span className="required">*</span>To Date (DD-MM-YYYY)
                                </label>
                                {errors.toDate && <div className="error-message">{errors.toDate}</div>}
                            </div>
                        </div>
                    </div>

                    <div className="leave-approval-btn-div">
                        <button
                            type="submit"
                            className="leave-approval-btn"
                            disabled={!isFetchDataEnabled}
                        >
                            Fetch Data
                        </button>
                        <button
                            type="button"
                            className="leave-approval-btn"
                            onClick={handleDownloadExcel}
                            disabled={attendanceData.length === 0}
                            style={{ marginLeft: '10px' }}
                        >
                            Download Excel
                        </button>
                    </div>
                </form>
            </div>

            <div className="approval-table-container">
                <table className="approval-table">
                    <thead>
                        <tr>
                            <th>S.No</th>
                            <th>District Name</th>
                            <th>Employee Name</th>
                            <th>Mobile Number</th>
                            <th>Designation</th>
                            <th>Client</th>
                            <th>Project</th>
                            <th>From Date</th>
                            <th>To Date</th>
                            <th>Days Applied</th>
                            <th>Leave Type</th>
                            <th>Reason</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {attendanceData.length > 0 ? (
                            attendanceData.map((item, index) => (
                                <tr key={item.id}>
                                    <td>{index + 1}</td>
                                    <td>{item.district}</td>
                                    <td>{item.employeeName}</td>
                                    <td>{item.contactNumber}</td>
                                    <td>{item.designation}</td>
                                    <td>{item.clientName}</td>
                                    <td>{item.projectName}</td>
                                    <td>{item.fromDate.split("-").reverse().join("-")}</td>
                                    <td>{item.toDate.split("-").reverse().join("-")}</td>
                                    <td>{item.daysApplied}</td>
                                    <td>{item.type}</td>
                                    <td>{item.reason}</td>
                                    <td>{item.status}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="13" style={{ textAlign: "center" }}>
                                    No data available
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default LeaveApprovalList;